import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { useOutsideClickClose } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/popover/useOutsideClickClose'

import { InsertMessageOptions } from './BaseChat'
import { getClientProfileMessengerTarget } from './data/messagesSelectors'
import { setMessageCenterTarget } from './data/messagesSlice'
import { MessengerThread } from './data/types'
import { useChatEntryPointFromRoute } from './hooks/useChatEntryPointFromRoute'
import useNavigateToClientAssignment from './hooks/useNavigateToClientAssignment'
import useNavigateToClientNote from './hooks/useNavigateToClientNote'
import { useRestorePreviousConversation } from './hooks/useRestorePreviousConversation'
import styles from './messagesPopover.module.scss'
import BaseMessenger from './messenger/BaseMessenger'
import { MESSENGER } from '../../common/constants/routingConstants'
import { getAuthUserId } from '../../data/auth/authSelectors'
import { getSelectedClientId } from '../../data/lyraTherapy/clientSelectors'
import { useAppDispatch } from '../../data/store'
import { setLastConversationClientId } from '../../data/userSettingsSlice'
import { useSelectedClientData } from '../clients/clientDetails/data/hooks/useSelectedClientData'
import { setToastContent } from '../data/ltToastAutoActions'

import type { MessagePopoverProps } from './MessagesPopover'

const noop = () => {}

// Space between the popover and the visible content edges.
const VIEWPORT_GUTTER = 16

export const ClientProfileMessagesPopover = ({
  closePopover,
  className,
  messagesPopoverContainer,
  entryPointOverride,
  selectedEpisodeId,
}: MessagePopoverProps) => {
  const selectedClientId = useSelector(getSelectedClientId)
  const target = useSelector(getClientProfileMessengerTarget)
  // Set by a Care services provider's message icon so the popover opens to that provider's conversation.
  const providerTarget = target?.clientId === selectedClientId ? target : undefined
  const providerPairCounterpart = providerTarget?.counterpart
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isFetching: isLoadingClient } = useSelectedClientData()
  const routeEntryPoint = useChatEntryPointFromRoute()
  const containerRef = useRef<HTMLDivElement>(null)
  const [messageToInsert, setMessageToInsert] = useState<{
    message: string
    opts?: InsertMessageOptions
  } | null>(null)
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null>(null)
  const [shouldInsertDBL, setShouldInsertDBL] = useState(false)
  const [shouldInsertEssentialsLink, setShouldInsertEssentialsLink] = useState(false)
  const navigateToAssignment = useNavigateToClientAssignment()
  const navigateToNote = useNavigateToClientNote()
  const providerId = useSelector(getAuthUserId)
  const { isReady, restorePreviousConversation } = useRestorePreviousConversation()

  const insertMessage = useCallback((message: string | null, opts?: InsertMessageOptions) => {
    setMessageToInsert(message ? { message, opts } : null)
    setCurrentTemplateId(message ? (opts?.attributesToAttach?.message_template_id ?? null) : null)
  }, [])

  const insertDirectBookingLink = useCallback(() => setShouldInsertDBL(true), [])
  const onDBLInserted = useCallback(() => setShouldInsertDBL(false), [])
  const insertEssentialsArticleChip = useCallback(() => setShouldInsertEssentialsLink(true), [])
  const onEssentialsLinkInserted = useCallback(() => setShouldInsertEssentialsLink(false), [])

  // Continue the selected thread and its unsent text in Message center.
  const openMessages = useCallback(
    (thread: MessengerThread | null, message: string) => {
      if (!selectedClientId) return
      restorePreviousConversation()
      dispatch(setLastConversationClientId(selectedClientId))
      if (thread) {
        dispatch(
          setMessageCenterTarget({
            clientId: selectedClientId,
            providerId,
            threadId: thread.id,
            // A provider without a conversation yet needs its draft thread in Message center too.
            draftCounterpart:
              !thread.conversationId && thread.recipientId === providerPairCounterpart?.participant_lyra_id
                ? providerPairCounterpart
                : undefined,
            message,
          }),
        )
      }
      closePopover()
      navigate(MESSENGER.route)
    },
    [
      closePopover,
      dispatch,
      navigate,
      providerId,
      providerPairCounterpart,
      restorePreviousConversation,
      selectedClientId,
    ],
  )

  const onProviderPairUnavailable = useCallback(() => {
    closePopover()
    dispatch(
      setToastContent({
        id: 'care-services-pair-unavailable',
        text: formatMessage({
          defaultMessage: 'Unable to open this conversation. Please try again.',
          description: 'Recoverable current provider messaging error',
        }),
        toastType: 'error',
      }),
    )
  }, [closePopover, dispatch, formatMessage])

  const onMouseDownCapture = useOutsideClickClose(containerRef, messagesPopoverContainer, true, closePopover)

  // Keep the right-anchored popover inside the viewport. The width also accounts for
  // the sidebar through the inherited --provider-content-left CSS variable.
  const [popoverLayout, setPopoverLayout] = useState<{ availableWidth: number; offsetX: number }>()
  useLayoutEffect(() => {
    const measure = () => {
      const container = containerRef.current
      const popover = container?.firstElementChild
      if (!container || !popover) return

      // Measure the anchor rather than the translated popover so repeated resizes
      // do not include the previous correction in the next position calculation.
      const rightOffset = parseFloat(window.getComputedStyle(popover).right) || 0
      const anchoredRight = container.getBoundingClientRect().right - rightOffset
      const visibleRight = Math.min(anchoredRight, document.documentElement.clientWidth - VIEWPORT_GUTTER)
      setPopoverLayout({
        availableWidth: Math.max(0, Math.floor(visibleRight - VIEWPORT_GUTTER)),
        offsetX: visibleRight - anchoredRight,
      })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
    // The popover element mounts once the messenger is ready.
  }, [isReady])

  return (
    <div
      ref={containerRef}
      onMouseDownCapture={onMouseDownCapture}
      className={`popover-container ${styles['popover-container']} ${styles['client-profile-messenger']} ${
        className?.includes('messaging-hub-popover') ? styles['messaging-hub-popover'] : ''
      }`}
      style={
        popoverLayout === undefined
          ? undefined
          : ({
              '--client-profile-messenger-available-width': `${popoverLayout.availableWidth}px`,
              '--client-profile-messenger-offset-x': `${popoverLayout.offsetX}px`,
            } as React.CSSProperties)
      }
      data-testid='ClientProfileMessagesPopover'
    >
      {isReady && (
        <BaseMessenger
          initialProviderPairCounterpart={providerPairCounterpart}
          // Each provider click reselects that provider, even if the popover is already open.
          providerPairRequestId={providerTarget?.requestId}
          onInitialProviderPairUnavailable={onProviderPairUnavailable}
          selectedClientId={selectedClientId}
          selectedEpisodeId={selectedEpisodeId}
          isComposing={false}
          onSelectedClientIdChanged={noop}
          showEmptyView={!selectedClientId}
          onIsComposingChanged={noop}
          onOpenClientProfile={closePopover}
          onOpenAssignment={(assignmentId) => {
            if (selectedClientId) {
              closePopover()
              navigateToAssignment(selectedClientId, assignmentId)
            }
          }}
          onOpenNotes={(noteId) => {
            if (selectedClientId) {
              closePopover()
              navigateToNote(selectedClientId, noteId)
            }
          }}
          initialDetailPanelOpen={false}
          isLoadingClient={isLoadingClient}
          entryPoint={entryPointOverride ?? routeEntryPoint}
          messageToInsert={messageToInsert}
          currentTemplateId={currentTemplateId}
          insertMessage={insertMessage}
          setMessageToInsert={setMessageToInsert}
          shouldInsertDBL={shouldInsertDBL}
          onDBLInserted={onDBLInserted}
          insertDirectBookingLink={insertDirectBookingLink}
          shouldInsertEssentialsLink={shouldInsertEssentialsLink}
          onEssentialsLinkInserted={onEssentialsLinkInserted}
          insertEssentialsArticleChip={insertEssentialsArticleChip}
          onClose={closePopover}
          onOpenMessages={openMessages}
          showClientThreadList
        />
      )}
    </div>
  )
}
